import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Package, Loader2 } from 'lucide-react';

interface ProgramPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  program: {
    id: string;
    title: string;
    description: string | null;
    session_count: number;
    total_tokens_required: number;
    discount_percentage: number;
  };
  mentorId: string;
  mentorName: string;
  studentBalance: number;
  onPurchaseSuccess: () => void;
}

export const ProgramPurchaseModal = ({
  isOpen,
  onClose,
  program,
  mentorId,
  mentorName,
  studentBalance,
  onPurchaseSuccess,
}: ProgramPurchaseModalProps) => {
  const [isPurchasing, setIsPurchasing] = useState(false);

  const hasEnoughTokens = studentBalance >= program.total_tokens_required;

  const handlePurchase = async () => {
    if (!hasEnoughTokens) {
      toast.error('Insufficient tokens', {
        description: `You need ${program.total_tokens_required} tokens but only have ${studentBalance}. Purchase more tokens first.`,
      });
      return;
    }

    setIsPurchasing(true);

    try {
      // Get current user
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        throw new Error('Not authenticated');
      }

      // Check if user already has an active enrollment for this program
      const { data: existingEnrollment } = await supabase
        .from('program_enrollments')
        .select('id')
        .eq('program_id', program.id)
        .eq('student_id', session.user.id)
        .eq('is_active', true)
        .maybeSingle();

      if (existingEnrollment) {
        toast.error('Already enrolled', {
          description: 'You already have an active enrollment in this program.',
        });
        setIsPurchasing(false);
        return;
      }

      // Deduct tokens from student balance
      const { error: deductError } = await supabase.rpc('spend_credits', {
        p_user_id: session.user.id,
        p_credits_to_spend: program.total_tokens_required,
      });

      if (deductError) throw deductError;

      // Create program enrollment
      const { error: enrollmentError } = await supabase
        .from('program_enrollments')
        .insert({
          program_id: program.id,
          student_id: session.user.id,
          mentor_id: mentorId,
          sessions_remaining: program.session_count,
          tokens_paid: program.total_tokens_required,
        });

      if (enrollmentError) throw enrollmentError;

      toast.success('Program purchased!', {
        description: `You now have ${program.session_count} sessions with ${mentorName}. Use them when booking!`,
      });

      onPurchaseSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error purchasing program:', error);
      toast.error('Purchase failed', {
        description: error.message || 'Failed to purchase program. Please try again.',
      });
    } finally {
      setIsPurchasing(false);
    }
  };

  const tokensPerSession = (program.total_tokens_required / program.session_count).toFixed(0);
  const savingsPerSession = program.discount_percentage > 0 
    ? (program.total_tokens_required / program.session_count * (program.discount_percentage / 100)).toFixed(0)
    : 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Package className="w-5 h-5 text-primary" />
            </div>
            <DialogTitle>Purchase Program</DialogTitle>
          </div>
          <DialogDescription>
            Get a bundle of sessions with {mentorName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="border rounded-lg p-4 space-y-3">
            <h4 className="font-semibold text-lg">{program.title}</h4>
            {program.description && (
              <p className="text-sm text-muted-foreground">{program.description}</p>
            )}
            
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-muted/50 rounded p-3">
                <div className="text-muted-foreground mb-1">Sessions</div>
                <div className="font-semibold text-lg">{program.session_count}</div>
              </div>
              <div className="bg-muted/50 rounded p-3">
                <div className="text-muted-foreground mb-1">Total Cost</div>
                <div className="font-semibold text-lg">{program.total_tokens_required} tokens</div>
              </div>
            </div>

            <div className="text-xs text-muted-foreground space-y-1">
              <div>≈ {tokensPerSession} tokens per session</div>
              {program.discount_percentage > 0 && (
                <div className="text-green-600 font-medium">
                  Save {savingsPerSession} tokens per session ({program.discount_percentage}% off)
                </div>
              )}
            </div>
          </div>

          <div className="bg-muted/30 rounded-lg p-3 text-sm">
            <div className="flex justify-between mb-1">
              <span className="text-muted-foreground">Your balance:</span>
              <span className="font-medium">{studentBalance} tokens</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">After purchase:</span>
              <span className={`font-medium ${!hasEnoughTokens ? 'text-destructive' : ''}`}>
                {studentBalance - program.total_tokens_required} tokens
              </span>
            </div>
          </div>

          {!hasEnoughTokens && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 text-sm text-destructive">
              You don't have enough tokens to purchase this program.
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={onClose}
              disabled={isPurchasing}
            >
              Cancel
            </Button>
            <Button
              className="flex-1"
              onClick={handlePurchase}
              disabled={!hasEnoughTokens || isPurchasing}
            >
              {isPurchasing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                `Purchase for ${program.total_tokens_required} tokens`
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
